import React from 'react'
import PropTypes from 'prop-types'
import {
  OutlinedButton,
  HeaderOne,
  HeaderThree,
  TextLink,
} from 'secondstep-components'
import ClassCard from 'components/dashboard/ClassCard'
import CreateCourse from 'experienceComponents/CreateCourse'
import {
  StyledBackpackV2Icon,
  StyledTelescopeIcon,
  Wrapper,
} from './component.styles'
import {
  EXPLORE_PROGRAM_DESCRIPTION,
  EXPLORE_PROGRAM_TITLE,
  MY_CLASSES_TITLE,
  TEACH_DESCRIPTION,
  NO_CLASSES_MESSAGE,
  NO_CLASSES_TRAINING_LINK,
} from './constants'

const ClassList = ({ classes, courseLinks, onClassClick, program }) => {
  return (
    <ul className="class-links">
      {classes.map(cl => {
        const course = courseLinks.find(c => cl?.contentfulID === c?.entryId)
        return (
          <li key={`class-card-li-${cl?.instance}`}>
            <ClassCard
              className={cl?.title}
              dataTestId={`class-card-${cl?.instance}`}
              displayTitle={cl?.title}
              gradeDisplayTitle={course?.title}
              instance={cl?.instance}
              onClick={onClassClick(course?.slug, cl?.instance)}
              program={program}
              scoID={cl?.scoID}
              totalAmount={cl?.totalLessons}
              totalComplete={cl?.completeLessons}
            />
          </li>
        )
      })}
    </ul>
  )
}

ClassList.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoID: PropTypes.number,
      totalLessons: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      entryId: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  onClassClick: PropTypes.func.isRequired,
  program: PropTypes.string,
}

const PreviewCourseList = ({ courseLinks, onCourseClick }) => {
  return (
    <ul className="course-links">
      {courseLinks &&
        courseLinks
          .filter(c => c.shouldShowOnLessonsPage)
          .map(c => (
            <li key={c.slug}>
              <OutlinedButton onClick={onCourseClick(c.slug)}>
                {c.title}
              </OutlinedButton>
            </li>
          ))}
    </ul>
  )
}

PreviewCourseList.propTypes = {
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  onCourseClick: PropTypes.func.isRequired,
}

export const ProductLessons = React.forwardRef(
  (
    {
      classes,
      program,
      courseLinks,
      onClassClick,
      onCourseClick,
      title,
      contentfulCourses,
    },
    ref,
  ) => {
    const userHasClasses = !!classes && classes.length > 0
    const userDoesNotHaveClasses = !userHasClasses

    return (
      <Wrapper>
        <header>
          <HeaderOne>{title}</HeaderOne>
        </header>

        <section>
          <header>
            <div className="spot">
              <StyledBackpackV2Icon aria-hidden="true" role="presentation" />
            </div>
            <div className="header-text">
              <div className="headline">
                <HeaderThree>{MY_CLASSES_TITLE}</HeaderThree>
                <div className="spacer" />
                {userHasClasses && (
                  <CreateCourse
                    contentfulCourses={contentfulCourses}
                    ref={ref}
                  />
                )}
              </div>
              <p>{TEACH_DESCRIPTION}</p>
            </div>
          </header>
          <div>
            {userDoesNotHaveClasses && (
              <div className="no-classes">
                <p className="no-class-description">{NO_CLASSES_MESSAGE}</p>
                <p>
                  {NO_CLASSES_TRAINING_LINK}{' '}
                  <TextLink href="/dashboard">My Programs</TextLink>.
                </p>
                <CreateCourse
                  contentfulCourses={contentfulCourses}
                  emptyClasses={true}
                  ref={ref}
                />
              </div>
            )}
            {userHasClasses && (
              <ClassList
                classes={classes}
                courseLinks={courseLinks}
                onClassClick={onClassClick}
                program={program}
              />
            )}
          </div>
        </section>
        <section id="explore-the-program">
          <header>
            <div className="spot">
              <StyledTelescopeIcon aria-hidden="true" role="presentation" />
            </div>
            <div>
              <HeaderThree>{EXPLORE_PROGRAM_TITLE}</HeaderThree>
              <p>{EXPLORE_PROGRAM_DESCRIPTION}</p>
            </div>
          </header>

          <PreviewCourseList
            courseLinks={courseLinks}
            onCourseClick={onCourseClick}
          />
        </section>
      </Wrapper>
    )
  },
)

ProductLessons.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoId: PropTypes.number,
      totalLessons: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  contentfulCourses: PropTypes.arrayOf(
    PropTypes.shape({
      displayTitle: PropTypes.string,
    }),
  ).isRequired,
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  onClassClick: PropTypes.func.isRequired,
  onCourseClick: PropTypes.func.isRequired,
  program: PropTypes.string,
  title: PropTypes.string.isRequired,
}
export default ProductLessons
